import { rem } from 'polished';
import React, { MouseEventHandler, useState, useContext, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { useABLincoln } from '@vimeo/ablincoln';
import { Event, BigPictureClient } from '@vimeo/bigpicture-client';
import { translate } from '@vimeo/i18n-runtime';
import { blue, slate } from '@vimeo/iris/color';
import { Checkbox } from '@vimeo/iris/components';
import { Paragraph as P, Link } from '@vimeo/iris/typography';
import { useTurnstile } from '@vimeo/turnstile';
import { ViewerContext } from '@vimeo/viewer';

import {
  BpData,
  trackJoinWithGoogleClick,
  trackLogin,
  trackLoginWithSocialMediaClick,
  trackMarketingTermCheckbox,
  trackRegistration,
} from '../tracking';
import { JoinEmailForm, LoginEmailForm } from './emailForms';
import { HR, OrSeparator } from './emailForms/Shared';
import RenderMarketingCheckBox from './MarketingCheckbox';
import { FacebookForm, GoogleForm, AppleForm } from './socialForms';

enum ABTestOptions {
  control = 'control',
  variant = 'variant',
}

const FormSection = styled.section`
  margin-top: ${rem(12)};
  display: flex;
  flex-direction: column;
`;

const Paragraph = styled(P)<{ faded?: boolean; addBottomMargin?: boolean }>`
  margin-left: 50%; /*for ie11*/
  margin: auto;
  text-align: center;
  ${(props) =>
    props.faded &&
    css`
      color: ${slate(500)};
    `}
  ${(props) =>
    props.addBottomMargin &&
    css`
      margin-bottom: ${rem(8)};
    `}
`;

const CheckboxContainer = styled.div`
  margin-top: ${rem(8)};
`;

const FacebookConnectFormSection = styled(FormSection)`
  margin: ${rem(35)} 0 ${rem(41)} 0;
`;

const TermsLink = styled(Link)`
  text-decoration: underline;
`;

const LinkText = styled.button`
  color: ${blue(500)};
  cursor: pointer;
  background: transparent;
  border: none;
  padding: 0;
  font-size: ${rem(14)};
`;

const defaultTermsAndConditions = translate({
  singular:
    'By joining Vimeo, you agree to our {TERMS_LINK}Terms of Service{/TERMS_LINK}, {PRIVACY_LINK}Privacy Policy{/PRIVACY_LINK} and {COOKIE_LINK}Cookie Policy{/COOKIE_LINK}.',
  replacements: {
    TERMS_LINK: (text: string) => (
      <TermsLink href="/terms" key="terms" target="_blank" variant="minimal" format="soft">
        {text}
      </TermsLink>
    ),
    PRIVACY_LINK: (text: string) => (
      <TermsLink href="/privacy" key="privacy" target="_blank" variant="minimal" format="soft">
        {text}
      </TermsLink>
    ),
    COOKIE_LINK: (text: string) => (
      <TermsLink href="/cookie_policy" key="cookie" target="_blank" variant="minimal" format="soft">
        {text}
      </TermsLink>
    ),
  },
});

export interface FormProps {
  isTwoStep?: boolean;
  email?: string;
  fa?: {
    container: string;
    thirdPartyIntegration?: string;
  };
  name?: string;
  disableEmail?: boolean;
  onSuccess?: (response: any) => void;
  redirectUrl?: string;
  shouldOptIntoMarketing?: boolean;
  switchType?: MouseEventHandler;
  xsrft: string;
  onEmailChange?: (email: string) => void;
  redirectUrlAfterSocialLogin?: string;
  socialSubmitBtn?: ReactNode;
  onSocialLoginSubmit?: (event: any) => void;
  onSocialLoginClick?: (
    name: string,
    event?: {
      container: string;
      thirdPartyIntegration?: string;
    },
  ) => void;
  target?: string;
  termsAndConditions?: string;
  disclaimer?: string;
  onSSOFlowChoice?: (activateSSOFlow: boolean) => void;
  placeholder?: {
    email: string;
    name?: string;
    password: string;
  };
  submitCtaText?: string;
  showEnterpriseOptInConsent?: boolean;
  className?: string;
  hasF2PVariant?: boolean;
  isF2PVariant?: boolean;
  isSimplifiedSite?: boolean;
  isFullScreen?: boolean;
}

export function JoinForm({
  email,
  fa,
  name,
  onSuccess,
  redirectUrl,
  shouldOptIntoMarketing = true,
  switchType,
  xsrft,
  onEmailChange,
  redirectUrlAfterSocialLogin = '',
  disableEmail = false,
  socialSubmitBtn,
  onSocialLoginSubmit,
  onSocialLoginClick,
  target,
  termsAndConditions,
  disclaimer,
  onSSOFlowChoice,
  placeholder,
  hasF2PVariant,
  isFullScreen,
}: FormProps) {
  const [marketingOptIn, setMarketingOptIn] = useState(shouldOptIntoMarketing);
  const marketingOptInHandler = () => {
    setMarketingOptIn(!marketingOptIn);
    trackMarketingTermCheckbox(!marketingOptIn, false);
  };

  const showSSOSignInButton =
    useABLincoln()('TopNav.SignInWithSSO', 'show_sso_button', ABTestOptions.control) ===
    ABTestOptions.variant;
  const signInWithSSOClick = () => {
    if (onSSOFlowChoice) {
      onSSOFlowChoice(true);
    }

    BigPictureClient.sendEvent(
      new Event('vimeo.click', 17, {
        page: 'logged_in_homepage',
        location: 'login_modal',
        target: 'login_with_sso_flow',
        name: 'click_signin_with_sso',
        copy: 'Sign in with SSO',
        feature: 'login',
        type: 'general',
      }),
    );
  };

  const socialClickHandler = onSocialLoginClick ?? trackRegistration;

  termsAndConditions = termsAndConditions ? termsAndConditions : defaultTermsAndConditions;

  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [turnstileError, setTurnstileError] = useState<string | undefined>(undefined);

  const turnstileErrorCallback = () => {
    setCaptchaVisible(true);
    setTurnstileError(
      translate({
        singular:
          'Whoops! We are having some technical difficulties. To continue, contact our {A}support team{/A}.',
        replacements: {
          A: (children: string) => (
            <Link href="https://vimeo.com/help/contact" target="_blank">
              {children}
            </Link>
          ),
        },
      }),
    );
  };

  const { turnstileToken } = useTurnstile(false, 'turnstile-container', {
    errorCb: turnstileErrorCallback,
    beforeInteractiveCb: () => setCaptchaVisible(true),
  });

  return (
    <>
      <FormSection>
        <JoinEmailForm
          xsrft={xsrft}
          redirectUrl={redirectUrl}
          optIn={marketingOptIn}
          name={name}
          email={email}
          disableEmail={disableEmail}
          onEmailChange={onEmailChange}
          fa={fa}
          onSuccess={onSuccess}
          placeholder={placeholder}
          termsAndConditions={termsAndConditions}
          disclaimer={disclaimer}
          hasF2PVariant={hasF2PVariant}
          marketingCheckBox={
            <RenderMarketingCheckBox
              hideHorizontalRule
              marketingOptIn={marketingOptIn}
              marketingOptInHandler={marketingOptInHandler}
            />
          }
          turnstileToken={turnstileToken}
          turnstileError={turnstileError}
          captchaVisible={captchaVisible}
          isFullScreen={isFullScreen}
        />
      </FormSection>
      {showSSOSignInButton && (
        <FormSection>
          <Paragraph size="1">
            <Link onClick={signInWithSSOClick} element="span" format="soft">
              {translate('Sign in with SSO')}
            </Link>
          </Paragraph>
        </FormSection>
      )}
      {!hasF2PVariant && (
        <FormSection>
          <OrSeparator>{translate('or')}</OrSeparator>
        </FormSection>
      )}
      <FormSection>
        <GoogleForm
          xsrft={xsrft}
          action="join"
          optIn={marketingOptIn}
          redirectUrl={redirectUrl}
          onClick={() => {
            trackJoinWithGoogleClick(false, redirectUrl);
            socialClickHandler('google', fa);
          }}
          redirectUrlAfterSocialLogin={redirectUrlAfterSocialLogin}
          target={target}
          onSubmit={onSocialLoginSubmit}
          socialSubmitBtn={socialSubmitBtn}
          turnstileToken={turnstileToken}
        >
          {translate('Join with Google')}
        </GoogleForm>
      </FormSection>
      {!hasF2PVariant && (
        <RenderMarketingCheckBox
          hideHorizontalRule={false}
          marketingOptIn={marketingOptIn}
          marketingOptInHandler={marketingOptInHandler}
        />
      )}
      {switchType && (
        <FormSection>
          <Paragraph size="2" format="alternative" addBottomMargin>
            {translate('Already have an account?')}
            &nbsp;
            <LinkText onClick={switchType}>{translate('Log in')}</LinkText>
          </Paragraph>
        </FormSection>
      )}
      {!hasF2PVariant && (
        <FormSection>
          <Paragraph format="soft" size="3" className="termsandconditions">
            {termsAndConditions}
          </Paragraph>
          {disclaimer && <Paragraph size="3">{disclaimer}</Paragraph>}
        </FormSection>
      )}
    </>
  );
}

export function LoginForm({
  isTwoStep = false,
  xsrft,
  switchType,
  redirectUrl,
  email,
  fa,
  disableEmail,
  onSuccess,
  onEmailChange,
  redirectUrlAfterSocialLogin = '',
  onSocialLoginSubmit,
  onSocialLoginClick,
  target,
  onSSOFlowChoice,
  showEnterpriseOptInConsent,
  className,
  placeholder,
  submitCtaText,
  isF2PVariant,
  isSimplifiedSite,
  isFullScreen,
}: FormProps) {
  const socialClickHandler = onSocialLoginClick ?? trackLogin;
  const showSSOSignInButton =
    useABLincoln()('TopNav.SignInWithSSO', 'show_sso_button', ABTestOptions.control) ===
    ABTestOptions.variant;
  const signInWithSSOClick = () => {
    if (onSSOFlowChoice) {
      onSSOFlowChoice(true);
    }

    BigPictureClient.sendEvent(
      new Event('vimeo.click', 17, {
        page: 'logged_in_homepage',
        location: 'login_modal',
        target: 'login_with_sso_flow',
        name: 'click_signin_with_sso',
        copy: 'Sign in with SSO',
        feature: 'login',
        type: 'general',
      }),
    );
  };

  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);

  const formJSX = (
    <>
      <FormSection>
        <LoginEmailForm
          xsrft={xsrft}
          redirectUrl={redirectUrl}
          email={email}
          onEmailChange={onEmailChange}
          disableEmail={disableEmail}
          fa={fa}
          onSuccess={onSuccess}
          target={target}
          showEnterpriseOptInConsent={showEnterpriseOptInConsent}
          placeholder={placeholder}
          submitCtaText={submitCtaText}
          className={className}
          isF2PVariant={isF2PVariant}
          isTwoStep={isTwoStep}
          onTurnstileTokenChange={setTurnstileToken}
          isFullScreen={isFullScreen}
        />
      </FormSection>
      {showSSOSignInButton && (
        <FormSection>
          <Paragraph size="1">
            <Link onClick={signInWithSSOClick} element="span" format="soft">
              {translate('Sign in with SSO')}
            </Link>
          </Paragraph>
        </FormSection>
      )}
    </>
  );

  const socialLoginsJSX = (
    <>
      <FormSection>
        <FacebookForm
          xsrft={xsrft}
          action="login"
          optIn={null}
          redirectUrl={redirectUrl}
          target={target}
          onSubmit={onSocialLoginSubmit}
          onClick={() => {
            socialClickHandler('facebook', fa);
            trackLoginWithSocialMediaClick('facebook', isTwoStep, redirectUrl);
          }}
          className={className}
          turnstileToken={turnstileToken}
        >
          {translate('Log in with Facebook')}
        </FacebookForm>
      </FormSection>
      <FormSection>
        <GoogleForm
          xsrft={xsrft}
          action="login"
          optIn={null}
          redirectUrl={redirectUrl}
          onClick={() => {
            socialClickHandler('google', fa);
            trackLoginWithSocialMediaClick('google', isTwoStep, redirectUrl);
          }}
          onSubmit={onSocialLoginSubmit}
          target={target}
          redirectUrlAfterSocialLogin={redirectUrlAfterSocialLogin}
          className={className}
          turnstileToken={turnstileToken}
        >
          {translate('Log in with Google')}
        </GoogleForm>
      </FormSection>
      <FormSection>
        <AppleForm
          xsrft={xsrft}
          action="login"
          optIn={null}
          redirectUrl={redirectUrl}
          target={target}
          onSubmit={onSocialLoginSubmit}
          onClick={() => {
            socialClickHandler('apple', fa);
            trackLoginWithSocialMediaClick('apple', isTwoStep, redirectUrl);
          }}
          className={className}
          turnstileToken={turnstileToken}
        >
          {translate('Log in with Apple')}
        </AppleForm>
      </FormSection>
    </>
  );

  const separator = (
    <FormSection>
      <OrSeparator>{translate('or')}</OrSeparator>
    </FormSection>
  );

  let loginForm = [formJSX, separator, socialLoginsJSX];
  if (isTwoStep) {
    loginForm = isSimplifiedSite ? [formJSX] : [socialLoginsJSX, separator, formJSX];
  }

  return (
    <>
      {loginForm}
      {switchType && (
        <FormSection>
          <Paragraph size="2" format="alternative" addBottomMargin>
            {translate("Don't have an account?")}
            &nbsp;
            <LinkText onClick={switchType}>{translate('Join')}</LinkText>
          </Paragraph>
        </FormSection>
      )}
    </>
  );
}

export function ConnectToFbForm({
  onSuccess,
  switchType,
  xsrft,
  redirectUrl,
  shouldOptIntoMarketing = true,
  target,
}: FormProps) {
  const [marketingOptIn, setMarketingOptIn] = useState(shouldOptIntoMarketing);
  const viewer = useContext(ViewerContext);

  const marketingOptInHandler = () => {
    setMarketingOptIn(!marketingOptIn);
  };

  return (
    <>
      {switchType && (
        <Paragraph size="1" faded addBottomMargin>
          {translate('Already have an account?')}
          &nbsp;
          <Link onClick={switchType} element="span" variant="minimal">
            {translate('Log in')}
          </Link>
        </Paragraph>
      )}
      <FacebookConnectFormSection>
        <FacebookForm
          xsrft={xsrft}
          action="join"
          optIn={null}
          onClick={() => {
            BigPictureClient.sendEvent(
              new Event('click_to_signup_via_fb', 3, {
                product: 'Vimeo Create',
                cta: 'continue_with_facebook',
                path: window.location.pathname,
                location: 'signup',
                device_id: viewer?.vuid || null,
                lang: viewer?.locale || null,
                platform: window.navigator.platform,
                user_id: viewer?.user?.id.toString() || null,
                session_id: null,
                client_timestamp: null,
                client_ver: null,
                clients_session_start_ts: null,
                debug_mode: null,
                in_background: null,
                is_guest: null,
                flow: null,
                user_package_type: null,
              }),
            );
          }}
          redirectUrl={redirectUrl}
          target={target}
        >
          {translate('Continue with Facebook')}
        </FacebookForm>
      </FacebookConnectFormSection>
      <FormSection>
        <CheckboxContainer>
          <Checkbox
            checked={marketingOptIn}
            onChange={marketingOptInHandler}
            id="marketing_opt_in"
            label={
              <Paragraph size="3">
                {translate(
                  'I agree to receive newsletters, product updates, and exclusive offers from Vimeo.',
                )}
              </Paragraph>
            }
          />
        </CheckboxContainer>
        <HR />
      </FormSection>
      <FormSection>
        <Paragraph size="3">
          {translate({
            singular:
              'By joining Vimeo, you agree to our {TERMS_LINK}Terms of Service{/TERMS_LINK}, {PRIVACY_LINK}Privacy Policy{/PRIVACY_LINK} and {COOKIE_LINK}Cookie Policy{/COOKIE_LINK}.',
            replacements: {
              TERMS_LINK: (text: string) => (
                <Link href="/terms" key="terms" target="_blank" variant="minimal">
                  {text}
                </Link>
              ),
              PRIVACY_LINK: (text: string) => (
                <Link href="/privacy" key="privacy" target="_blank" variant="minimal">
                  {text}
                </Link>
              ),
              COOKIE_LINK: (text: string) => (
                <Link href="/cookie_policy" key="cookie" target="_blank" variant="minimal">
                  {text}
                </Link>
              ),
            },
          })}
        </Paragraph>
      </FormSection>
    </>
  );
}
